import {
  Container,
  PageHeader,
  Title,
  BackButton,
  HeroCard,
  HeroRow,
  HeroLabel,
  HeroValue,
  HeroDescription,
  InviteButton,
  Relative,
  PopupContainer,
  PopupTitle,
  PopupText,
  SectionContainer,
  Section,
  DateLabelContainer,
  DateLabel,
  DateLabelRow,
  EarningContainer,
  Earning,
  Chevron,
  LeaderboardContainer,
  LeaderboardItem,
  LeaderboardEmpty,
  LeaderboardName,
  EmptyContainer,
  EmptyContent,
  EmptyTitleContainer,
  EmptyTitle,
  EmptySubtitle,
  ReferralLinkContainer,
  ReferralLink,
  CopyButton,
  LightText,
  ShareButton,
  LoadingContainer,
  CardsGrid,
  StatCard,
  StatCardHeader,
  StatCardHeaderInfo,
  StatCardTitle,
  StatCardSubtitle,
  StatRow,
  StatLabel,
  StatValue,
  HistorySection,
  HistoryHeader,
  HistoryTitle,
  HistoryList,
  HistoryLoading,
  HistoryEmpty,
  HistoryTableHeader,
  HistoryRow,
  HistoryCell,
  HistoryEmpty as TemplateEmpty,
  TableFooter,
  FooterText,
  Pagination,
  PaginationButton,
  PaginationCurrent,
  TemplateRow,
  MobileTemplateRow,
  TemplateColumn,
  TemplateLabel,
  TemplateValue,
  WithdrawCard,
  WithdrawSummary,
  WithdrawLabel,
  WithdrawAmount,
  WithdrawHint,
  WithdrawButton,
  StatUsdHint,
} from "./ReferralEarningsPage.styles";

import DropIcon from "../../components/DropIcon/DropIcon";
import MainContainer from "../../components/MainContainer";
import { useNavigate } from "react-router-dom";

import { ReactComponent as InfoSvg } from "../../assets/icons/info-circle.svg";
import { ReactComponent as ChevronSvg } from "../../assets/icons/chevronLeft.svg";
import { ReactComponent as LinkSvg } from "../../assets/icons/link.svg";
import { ReactComponent as CopySvg } from "../../assets/icons/copy.svg";
import { ReactComponent as SuccessSvg } from "../../assets/icons/success.svg";
import { ReactComponent as UsersSvg } from "../../assets/icons/users.svg";
import { ReactComponent as TemplatesSvg } from "../../assets/icons/templates.svg";

import ReferralModal from "../../components/ReferralModal/ReferralModal";
import RequestWithdrawalModal from "../../components/RequestWithdrawalModal/RequestWithdrawalModal";
import { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getReferralUsers } from "../../store/slices/user/asyncThunk";
import {
  selectGetReferralUsersPending,
  selectReferralEarnings,
  selectUser,
} from "../../store/slices/user/slice";
import { selectRequestWithdrawal } from "../../store/slices/payment/slice";
import { fetchRequestWithdrawal } from "../../store/slices/payment/asyncThunk";
import { toast } from "sonner";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import {
  formatDate,
  formatTimeAgo,
  getMonthName,
  priceFormatter,
  priceFormatterDecimal,
} from "../../utils/utils";

const ReferralEarningsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [openSections, setOpenSections] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(true);
  const [isTemplatesExpanded, setIsTemplatesExpanded] = useState(true);
  const [templatesPage, setTemplatesPage] = useState(1);
  const TEMPLATES_PER_PAGE = 10;
  const REFERRAL_CREDITS_PER_USD = 50;
  const TEMPLATE_CREDITS_PER_USD = 150;
  const MIN_WITHDRAWAL_USD = 100;
  const popupRef = useRef(null);
  const { data } = useSelector(selectReferralEarnings);

  const [copySuccess, setCopySuccess] = useState(false);
  const user = useSelector(selectUser);
  const referralLink = user?.affiliateLink;
  const isLoggedIn = useSelector(selectLoggedIn);

  const isLoading = useSelector(selectGetReferralUsersPending);
  const requestWithdrawalData = useSelector(selectRequestWithdrawal);

  const creditsHistoryPage = requestWithdrawalData.page;
  const creditsHistoryTotal = requestWithdrawalData.total;
  const creditsHistoryPerPage = 10;

  const hasTemplateEarnings =
    parseInt(user?.wallet?.templateBalance?.lifetime ?? 0) > 0;

  const hasEarnedCredits =
    parseInt(user?.wallet?.referralBalance?.lifetime ?? 0) > 0 ||
    hasTemplateEarnings;

  const availableReferralCredits = parseInt(
    user?.wallet?.referralBalance?.available ?? 0,
  );
  const availableTemplateCredits = parseInt(
    user?.wallet?.templateBalance?.available ?? 0,
  );

  const availableReferralUsd =
    availableReferralCredits / REFERRAL_CREDITS_PER_USD;
  const availableTemplateUsd =
    availableTemplateCredits / TEMPLATE_CREDITS_PER_USD;
  const availableTotalUsd = availableReferralUsd + availableTemplateUsd;
  const canWithdraw = availableTotalUsd >= MIN_WITHDRAWAL_USD;

  const toggleSection = (monthKey) => {
    setOpenSections((prev) => ({
      ...prev,
      [monthKey]: !prev[monthKey],
    }));
  };

  const handleBack = () => {
    navigate("/");
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  };

  const handlePageChange = (nextPage) => {
    dispatch(fetchRequestWithdrawal({ nextPage }));
  };

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(getReferralUsers());
      dispatch(fetchRequestWithdrawal());
    }
  }, [dispatch, isLoggedIn]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        if (showPopup) {
          setShowPopup(false);
        }
      }
    };

    if (showPopup) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPopup]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopySuccess(true);
      toast.success("Referral link copied to clipboard!");
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy link:", err);
      toast.error("Failed to copy link. Please try again.");
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: "Join Fapify - NSFW AI Video Generation",
      text: "Create amazing NSFW AI videos with Fapify! Use my referral link to get started.",
      url: referralLink,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        toast.success("Invite shared successfully!");
      } else {
        await navigator.clipboard.writeText(
          `${shareData.text} ${shareData.url}`,
        );
        toast.success("Invite message copied to clipboard!");
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Error sharing:", err);
        toast.error("Failed to share invite. Please try again.");
      }
    }
  };

  if (!user) {
    return null;
  }

  return (
    <MainContainer>
      <Container>
        {/* Header */}
        <PageHeader>
          <Title>Affiliate</Title>
          <BackButton onClick={handleBack}>
            <ChevronSvg />
            Back
          </BackButton>
        </PageHeader>

        {isLoading ? (
          <LoadingContainer>
            <l-bouncy
              size="24"
              stroke="2"
              speed="1.5"
              color="var(--color-brand-500)"
            />
            <span>Loading earnings...</span>
          </LoadingContainer>
        ) : (
          <>
            {data && data?.length > 0 ? (
              <>
                {/* Hero Card */}
                <HeroCard>
                  <HeroRow>
                    <div>
                      <HeroLabel>
                        Referral Earnings
                        <Relative ref={popupRef}>
                          <InfoSvg
                            width={16}
                            height={16}
                            style={{
                              pointerEvents: showPopup ? "none" : "all",
                              cursor: "pointer",
                              opacity: 0.6,
                            }}
                            onClick={() => setShowPopup(true)}
                          />
                          <PopupContainer $isVisible={showPopup}>
                            <PopupTitle>Earn from Your Network!</PopupTitle>
                            <PopupText>
                              Invite users to Fapify and receive 30% of their
                              lifetime spend.
                            </PopupText>
                          </PopupContainer>
                        </Relative>
                      </HeroLabel>
                      <HeroValue>
                        {user?.usageOfAffiliate ?? 0} friends invited
                      </HeroValue>
                    </div>
                    <InviteButton onClick={() => setIsOpenModal(true)}>
                      Invite Friends
                    </InviteButton>
                  </HeroRow>
                  <HeroDescription>
                    Invite users to Fapify and receive 30% of their lifetime
                    spend as referral drops. Drops are tracked per month below.
                  </HeroDescription>
                </HeroCard>

                {/* Monthly Sections */}
                <SectionContainer>
                  {data?.map((month, sectionIndex) => {
                    const monthKey = `${month?.month}-${sectionIndex}`;
                    const isOpen = openSections[monthKey];
                    return (
                      <Section key={monthKey} $open={isOpen}>
                        <DateLabelContainer
                          onClick={() => toggleSection(monthKey)}
                          $open={isOpen}
                        >
                          <DateLabel>{getMonthName(month?.month)}</DateLabel>
                          <DateLabelRow>
                            <EarningContainer>
                              <DropIcon
                                size={14}
                                aria-hidden="true"
                                style={{ marginBottom: -2.5 }}
                              />
                              <Earning>
                                {priceFormatter(month?.totalCredits)}
                              </Earning>
                            </EarningContainer>
                            <Chevron className="chevron" />
                          </DateLabelRow>
                        </DateLabelContainer>

                        <LeaderboardContainer
                          $open={isOpen}
                          $itemCount={month?.logs?.length || 1}
                        >
                          {month?.logs?.length ? (
                            month.logs.map((log, index) => (
                              <LeaderboardItem key={`${monthKey}-${index}`}>
                                <LeaderboardName>
                                  @{log?.affiliateUser?.username ?? "anonymous"}
                                </LeaderboardName>
                                <EarningContainer>
                                  <DropIcon
                                size={14}
                                aria-hidden="true"
                                style={{ marginBottom: -2.5 }}
                              />
                                  <Earning>
                                    {priceFormatter(log?.numOfCredits)}
                                  </Earning>
                                </EarningContainer>
                              </LeaderboardItem>
                            ))
                          ) : (
                            <LeaderboardEmpty>
                              No individual earnings recorded for this month.
                            </LeaderboardEmpty>
                          )}
                        </LeaderboardContainer>
                      </Section>
                    );
                  })}
                </SectionContainer>
              </>
            ) : (
              <EmptyContainer>
                <EmptyContent>
                  <LinkSvg />
                  <EmptyTitleContainer>
                    <EmptyTitle>Earn from Your Network!</EmptyTitle>
                    <EmptySubtitle>
                      Invite users to Fapify and receive 30% of their lifetime
                      spend.
                    </EmptySubtitle>
                  </EmptyTitleContainer>

                  <ReferralLinkContainer>
                    <ReferralLink>
                      <span>{referralLink?.replace("https://www.", "")}</span>
                    </ReferralLink>
                    <CopyButton onClick={handleCopyLink} $success={copySuccess}>
                      {copySuccess ? <SuccessSvg /> : <CopySvg />}
                    </CopyButton>
                  </ReferralLinkContainer>
                  <LightText>or</LightText>
                  <ShareButton onClick={handleShare}>
                    Invite Friends
                  </ShareButton>
                </EmptyContent>
              </EmptyContainer>
            )}
          </>
        )}

        {/* Referral Balance Card */}
        <CardsGrid>
          <StatCard>
            <StatCardHeader>
              <StatCardHeaderInfo>
                <StatCardTitle>Referral Balance</StatCardTitle>
                <StatCardSubtitle>
                  Earned from users you invited · 50{" "}
                  <DropIcon
                                size={14}
                                aria-hidden="true"
                                style={{ marginBottom: -2.5 }}
                              />{" "}
                  : $1
                </StatCardSubtitle>
              </StatCardHeaderInfo>
              <UsersSvg height={22} width={22} />
            </StatCardHeader>
            <StatRow>
              <StatLabel>Available</StatLabel>
              <StatValue>
                {availableReferralCredits.toLocaleString("en-US")}{" "}
                <DropIcon
                                size={14}
                                aria-hidden="true"
                                style={{ marginBottom: -2.5 }}
                              />
                <StatUsdHint>≈ ${availableReferralUsd.toFixed(2)}</StatUsdHint>
              </StatValue>
            </StatRow>
            <StatRow>
              <StatLabel $dim>Pending</StatLabel>
              <StatValue $dim>
                {parseInt(
                  user?.wallet?.referralBalance?.pending ?? 0,
                ).toLocaleString("en-US")}{" "}
                <DropIcon
                                size={14}
                                aria-hidden="true"
                                style={{ marginBottom: -2.5 }}
                              />
              </StatValue>
            </StatRow>
            <StatRow>
              <StatLabel $dim>Lifetime</StatLabel>
              <StatValue $dim>
                {parseInt(
                  user?.wallet?.referralBalance?.lifetime ?? 0,
                ).toLocaleString("en-US")}{" "}
                <DropIcon
                                size={14}
                                aria-hidden="true"
                                style={{ marginBottom: -2.5 }}
                              />
              </StatValue>
            </StatRow>
          </StatCard>

          {hasTemplateEarnings && (
            <StatCard>
              <StatCardHeader>
                <StatCardHeaderInfo>
                  <StatCardTitle>Template Balance</StatCardTitle>
                  <StatCardSubtitle>
                    Earned from template sales · 150{" "}
                    <DropIcon
                                size={14}
                                aria-hidden="true"
                                style={{ marginBottom: -2.5 }}
                              />{" "}
                    : $1
                  </StatCardSubtitle>
                </StatCardHeaderInfo>
                <TemplatesSvg height={22} width={22} />
              </StatCardHeader>
              <StatRow>
                <StatLabel>Available</StatLabel>
                <StatValue>
                  {availableTemplateCredits.toLocaleString("en-US")}{" "}
                  <DropIcon
                                size={14}
                                aria-hidden="true"
                                style={{ marginBottom: -2.5 }}
                              />
                  <StatUsdHint>
                    ≈ ${availableTemplateUsd.toFixed(2)}
                  </StatUsdHint>
                </StatValue>
              </StatRow>
              <StatRow>
                <StatLabel $dim>Pending</StatLabel>
                <StatValue $dim>
                  {parseInt(
                    user?.wallet?.templateBalance?.pending ?? 0,
                  ).toLocaleString("en-US")}{" "}
                  <DropIcon
                                size={14}
                                aria-hidden="true"
                                style={{ marginBottom: -2.5 }}
                              />
                </StatValue>
              </StatRow>
              <StatRow>
                <StatLabel $dim>Lifetime</StatLabel>
                <StatValue $dim>
                  {parseInt(
                    user?.wallet?.templateBalance?.lifetime ?? 0,
                  ).toLocaleString("en-US")}{" "}
                  <DropIcon
                                size={14}
                                aria-hidden="true"
                                style={{ marginBottom: -2.5 }}
                              />
                </StatValue>
              </StatRow>
            </StatCard>
          )}
        </CardsGrid>

        {/* Withdraw summary */}
        <WithdrawCard>
          <WithdrawSummary>
            <WithdrawLabel>Available to withdraw</WithdrawLabel>
            <WithdrawAmount>${availableTotalUsd.toFixed(2)}</WithdrawAmount>
            <WithdrawHint>
              Minimum withdrawal: ${MIN_WITHDRAWAL_USD}. USDT on Solana.
            </WithdrawHint>
          </WithdrawSummary>
          <WithdrawButton
            onClick={() => setIsWithdrawModalOpen(true)}
            disabled={!canWithdraw}
          >
            Request Withdrawal
          </WithdrawButton>
        </WithdrawCard>

        {/* Published Templates */}
        {hasTemplateEarnings &&
          (() => {
            const approvedTrainModels = user?.approvedTrainModels;
            const templatesArray = approvedTrainModels
              ? Object.entries(approvedTrainModels)
              : [];
            const totalTemplates = templatesArray.length;
            const totalTemplatePages = Math.max(
              1,
              Math.ceil(totalTemplates / TEMPLATES_PER_PAGE),
            );
            const startIdx = (templatesPage - 1) * TEMPLATES_PER_PAGE;
            const paginatedTemplates = templatesArray.slice(
              startIdx,
              startIdx + TEMPLATES_PER_PAGE,
            );

            return (
              <HistorySection>
                <HistoryHeader
                  $expanded={isTemplatesExpanded}
                  onClick={() => setIsTemplatesExpanded(!isTemplatesExpanded)}
                >
                  <HistoryTitle>Published Templates</HistoryTitle>
                  <ChevronSvg
                    style={{
                      transition: "transform 0.3s ease-in-out",
                      transform: isTemplatesExpanded
                        ? "rotate(90deg)"
                        : "rotate(-90deg)",
                      cursor: "pointer",
                      opacity: 0.6,
                      width: 14,
                      height: 14,
                    }}
                  />
                </HistoryHeader>

                <HistoryList $expanded={isTemplatesExpanded}>
                  {totalTemplates === 0 ? (
                    <TemplateEmpty>
                      <span>You haven't published any templates yet.</span>
                    </TemplateEmpty>
                  ) : (
                    <>
                      {paginatedTemplates.map(([id, model], index) => {
                        const isLast = index === paginatedTemplates.length - 1;
                        return (
                          <Fragment key={id}>
                            <TemplateRow $isLast={isLast}>
                              <TemplateColumn>
                                <TemplateLabel>Template</TemplateLabel>
                                <TemplateValue>{model?.name}</TemplateValue>
                              </TemplateColumn>
                              <TemplateColumn $alignItems="center">
                                <TemplateLabel>First Earned</TemplateLabel>
                                <TemplateValue>
                                  {formatTimeAgo(model?.dateApproved)}
                                </TemplateValue>
                              </TemplateColumn>
                              <TemplateColumn $alignItems="flex-end">
                                <TemplateLabel>All</TemplateLabel>
                                <TemplateValue>
                                  {priceFormatter(model?.totalEarned ?? 0)}
                                  <DropIcon
                                size={14}
                                aria-hidden="true"
                                style={{ marginBottom: -2.5 }}
                              />
                                </TemplateValue>
                              </TemplateColumn>
                            </TemplateRow>

                            <MobileTemplateRow $isLast={isLast}>
                              <TemplateColumn $gap="8px">
                                <TemplateColumn>
                                  <TemplateLabel>Template</TemplateLabel>
                                  <TemplateValue>{model?.name}</TemplateValue>
                                </TemplateColumn>
                                <TemplateColumn $alignItems="flex-start">
                                  <TemplateLabel>First Earned</TemplateLabel>
                                  <TemplateValue>
                                    {formatTimeAgo(model?.dateApproved)}
                                  </TemplateValue>
                                </TemplateColumn>
                              </TemplateColumn>
                              <TemplateColumn $alignItems="flex-end">
                                <TemplateLabel>All</TemplateLabel>
                                <TemplateValue>
                                  {priceFormatter(model?.totalEarned ?? 0)}
                                  <DropIcon
                                size={14}
                                aria-hidden="true"
                                style={{ marginBottom: -2.5 }}
                              />
                                </TemplateValue>
                              </TemplateColumn>
                            </MobileTemplateRow>
                          </Fragment>
                        );
                      })}

                      <TableFooter>
                        <FooterText>
                          Showing {paginatedTemplates.length} of{" "}
                          {totalTemplates} templates
                        </FooterText>
                        {totalTemplates > TEMPLATES_PER_PAGE && (
                          <Pagination>
                            <PaginationButton
                              disabled={templatesPage <= 1}
                              onClick={() =>
                                setTemplatesPage((p) => Math.max(1, p - 1))
                              }
                            >
                              Previous
                            </PaginationButton>
                            <PaginationCurrent>
                              Page {templatesPage} of {totalTemplatePages}
                            </PaginationCurrent>
                            <PaginationButton
                              disabled={templatesPage >= totalTemplatePages}
                              onClick={() =>
                                setTemplatesPage((p) =>
                                  Math.min(totalTemplatePages, p + 1),
                                )
                              }
                            >
                              Next
                            </PaginationButton>
                          </Pagination>
                        )}
                      </TableFooter>
                    </>
                  )}
                </HistoryList>
              </HistorySection>
            );
          })()}

        {/* History */}
        {hasEarnedCredits && (
          <HistorySection>
            <HistoryHeader
              $expanded={isHistoryExpanded}
              onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
            >
              <HistoryTitle>History</HistoryTitle>
              <ChevronSvg
                style={{
                  transition: "transform 0.3s ease-in-out",
                  transform: isHistoryExpanded
                    ? "rotate(90deg)"
                    : "rotate(-90deg)",
                  cursor: "pointer",
                  opacity: 0.6,
                  width: 14,
                  height: 14,
                }}
              />
            </HistoryHeader>

            <HistoryList $expanded={isHistoryExpanded}>
              {requestWithdrawalData.loading ? (
                <HistoryLoading>
                  <l-bouncy
                    size="24"
                    stroke="2"
                    speed="1.5"
                    color="var(--color-brand-500)"
                  />
                  <span>Loading transaction history...</span>
                </HistoryLoading>
              ) : !requestWithdrawalData.data?.length ? (
                <HistoryEmpty>
                  <span>You have no transactions yet.</span>
                </HistoryEmpty>
              ) : (
                <>
                  <HistoryTableHeader>
                    <span>Description</span>
                    <span>Date</span>
                    <span>Amount</span>
                    <span>Status</span>
                  </HistoryTableHeader>

                  {requestWithdrawalData.data.map((item, index) => (
                    <HistoryRow key={index}>
                      <HistoryCell $bold $label="Description">
                        Request Withdrawal
                      </HistoryCell>
                      <HistoryCell $label="Date">
                        {formatDate(item?.createdAt)}
                      </HistoryCell>
                      <HistoryCell $bold $label="Amount">
                        ${priceFormatterDecimal(item?.amount)}
                      </HistoryCell>
                      <HistoryCell $capitalize $label="Status">
                        {item?.status}
                      </HistoryCell>
                    </HistoryRow>
                  ))}
                </>
              )}

              <TableFooter>
                <FooterText>
                  Showing {requestWithdrawalData.data?.length ?? 0} of{" "}
                  {creditsHistoryTotal} transactions
                </FooterText>
                <Pagination>
                  <PaginationButton
                    disabled={creditsHistoryPage <= 1}
                    onClick={() => handlePageChange(creditsHistoryPage - 1)}
                  >
                    Previous
                  </PaginationButton>
                  <PaginationCurrent>
                    Page {creditsHistoryPage} of{" "}
                    {Math.ceil(creditsHistoryTotal / creditsHistoryPerPage)}
                  </PaginationCurrent>
                  <PaginationButton
                    disabled={
                      creditsHistoryPage >=
                      Math.ceil(creditsHistoryTotal / creditsHistoryPerPage)
                    }
                    onClick={() => handlePageChange(creditsHistoryPage + 1)}
                  >
                    Next
                  </PaginationButton>
                </Pagination>
              </TableFooter>
            </HistoryList>
          </HistorySection>
        )}
      </Container>

      <ReferralModal
        isOpen={isOpenModal}
        onClose={() => setIsOpenModal(false)}
      />

      <RequestWithdrawalModal
        isOpen={isWithdrawModalOpen}
        onClose={() => setIsWithdrawModalOpen(false)}
        availableUsd={availableTotalUsd}
        onSuccess={() => dispatch(fetchRequestWithdrawal())}
      />
    </MainContainer>
  );
};

export default ReferralEarningsPage;
